// Age-group derivation (Stage A) — frontend mirror of backend/utils/age.js. Keep the two files
// in lockstep on any change; this repo has no shared server/client module for this logic.
//
// calcAge does a FULL calendar-date comparison (year/month/day) against "today" shifted into
// Kathmandu wall-clock — never year-subtraction. Both the stored DOB and "today" are read via UTC
// getters only, so this never depends on the viewer's browser timezone. Replaces the 3 duplicated
// local-time calcAge helpers previously in Students.jsx, MyStudents.jsx and TrialApplication.jsx.

export const KTM_OFFSET_MS = (5 * 60 + 45) * 60 * 1000; // Asia/Kathmandu = UTC+5:45

// "Now", shifted so its UTC getters read as Kathmandu wall-clock. Accepts an override for tests.
export function ktmNow(now = new Date()) {
  return new Date(now.getTime() + KTM_OFFSET_MS);
}

// Whole years of age as of "now" (KTM), or null for a missing/unparseable/future DOB.
export function calcAge(dob, now = new Date()) {
  if (!dob) return null;
  const birth = dob instanceof Date ? dob : new Date(dob);
  if (isNaN(birth.getTime())) return null;

  const by = birth.getUTCFullYear(), bm = birth.getUTCMonth() + 1, bd = birth.getUTCDate();
  const today = ktmNow(now);
  const ty = today.getUTCFullYear(), tm = today.getUTCMonth() + 1, td = today.getUTCDate();

  const isFuture = by > ty || (by === ty && bm > tm) || (by === ty && bm === tm && bd > td);
  if (isFuture) return null;

  let age = ty - by;
  if (tm < bm || (tm === bm && td < bd)) age--;
  return age;
}

// The 5 default rows — mirrors Settings.ageGroups' schema default. Used as an instant fallback
// while the real per-club rules are still loading (avoids a layout jump in AddStudentModal).
export const DEFAULT_AGE_GROUPS = [
  { name: 'Tiger',        minAge: 4,  maxAge: 4,    order: 1 },
  { name: 'Beginner',     minAge: 5,  maxAge: 7,    order: 2 },
  { name: 'Intermediate', minAge: 8,  maxAge: 13,   order: 3 },
  { name: 'Advanced',     minAge: 14, maxAge: 17,   order: 4 },
  { name: 'Adult',        minAge: 18, maxAge: null, order: 5 },
];

// Resolves an age (from calcAge) against a club's rules. Same contract as the backend version —
// see backend/utils/age.js for the full doc comment.
export function ageGroupFor(age, rules = DEFAULT_AGE_GROUPS) {
  if (age == null) return null;
  if (!Array.isArray(rules) || rules.length === 0) return null;

  const sorted = [...rules].sort((a, b) => a.order - b.order);
  const minConfigured = Math.min(...sorted.map(r => r.minAge));
  if (age < minConfigured) return { name: 'Too young', tooYoung: true };

  for (const r of sorted) {
    const hi = r.maxAge == null ? Infinity : r.maxAge;
    if (age >= r.minAge && age <= hi) return { name: r.name, minAge: r.minAge, maxAge: r.maxAge, order: r.order };
  }
  return null;
}
